import { ApiContact, UpsertContact, Contact } from './types';
import moment from 'moment-timezone';
import { HoursRow } from './types';
export const mapApiContactToContact = (c: ApiContact): Contact => {
  const cc = (c.country_code ?? '').trim();
  const pn = (c.phone_number ?? '').trim();
  const alreadyHasCC = pn.startsWith(cc) || pn.startsWith('+');
  const phone = pn
    ? alreadyHasCC
      ? pn
      : `${cc ? `${cc} ` : ''}${pn}`.trim()
    : '';

  return {
    id: c.id,
    kind: c.contact_type,
    name: c.contact_display_name || c.name || c.label || '—',
    email: c.email ?? '',
    phone,
    country: c.country_region || c.country_code || '',
    hours: c.operating_hours ?? '',
    note: c.notes || c?.usage_notes || '',
    isActive: c.is_primary_contact !== false,
    message: '',
    contact_type: c.contact_type,
    is_primary: c.is_primary_contact || false,
    is_primary_contact: c.is_primary_contact || false,
    default: c.default,
    api: {
      ...c,
      contact_source: 'manual',
      order_index: c.order_index,
      updated_at: c.updated_at,
      is_primary_contact: c.is_primary_contact,
    },
  };
};
export const mapApiToFormValues = (c: ApiContact | null) => {
  if (!c) return null;
  return c.contact_type === 'direct'
    ? {
        id: c.id,
        contact_type: 'direct',
        name: c.name ?? '',
        title: c.title ?? '',
        email: c.email ?? '',
        phone_number: c.phone_number ?? '',
        country_code: c.country_code ?? '',
        country_region: c.country_region ?? '',
        preferred_channel: c.preferred_channel ?? 'none',
        preferred_language: c.preferred_language ?? 'English',
        is_primary_contact: c.is_primary_contact ?? false,
        notes: c.notes ?? '',
      }
    : {
        id: c.id,
        contact_type: 'general_support',
        label: c.label ?? '',
        topic: c.topic ?? 'General',
        email: c.email ?? '',
        phone_number: c.phone_number ?? '',
        country_code: c.country_code ?? '',
        country_region: c.country_region ?? '',
        preferred_channel: c.preferred_channel ?? 'phone',
        operating_hours: c.operating_hours ?? '',
        timezone: c.timezone ?? '',
        typical_hold_time: c.typical_hold_time ?? '',
        notes: c.notes ?? '',
      };
};

export const mapApiContactToUpsertContact = (c: ApiContact): UpsertContact => {
  const cc = (c.country_code ?? '').trim();
  const pn = (c.phone_number ?? '').trim();
  const alreadyHasCC = pn.startsWith(cc) || pn.startsWith('+');
  const phone = pn
    ? alreadyHasCC
      ? pn
      : `${cc ? `${cc} ` : ''}${pn}`.trim()
    : '';

  return {
    id: c.id,
    name: c.contact_display_name || c.name || c.label || '—',
    email: c.email ?? '',
    phone,
    country: c.country_region || c.country_code || '',
    hours: c.operating_hours ?? '',
    note: c.notes || c?.usage_notes || '',
    isActive: c.is_primary_contact !== false,
    message: '',
    contact_type: c.contact_type || 'direct',
    api: c,
  };
};

export const upsertContactList = (
  prev: UpsertContact[] = [],
  apiContact: ApiContact,
  mapper: (c: ApiContact) => UpsertContact = mapApiContactToUpsertContact
): UpsertContact[] => {
  const id = String(apiContact?.id ?? '');
  const next = [...prev];
  const idx = next.findIndex((c) => String(c.id) === id);

  const newNote = apiContact?.notes ?? apiContact?.usage_notes ?? undefined;

  if (idx >= 0) {
    const old = next[idx];
    const mergedApi: ApiContact = {
      ...old.api,
      ...apiContact,
      ...(newNote !== undefined ? { notes: newNote } : {}),
    };
    const remapped = mapper(mergedApi);
    next[idx] = {
      ...old,
      ...remapped,
      ...(newNote !== undefined ? { note: newNote } : {}),
      api: mergedApi,
    };
  } else {
    const mapped = mapper({
      ...apiContact,
      ...(newNote !== undefined ? { notes: newNote } : {}),
    });
    next.push(mapped);
  }

  return next;
};

export const zones = moment.tz.names();

export const timeOptions = (() => {
  const opts: { label: string; value: string }[] = [];
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  for (let h = 0; h < 24; h++) {
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    const ampm = h < 12 ? 'am' : 'pm';
    opts.push({ label: `${hour12}${ampm}`, value: `${pad(h)}:00` });
  }
  return opts;
})();

export const dayOptions = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(
  (d) => ({
    label: d,
    value: d,
  })
);
export const inlineGap = 12;
const hhmmToLabel = (hhmm?: string | null) => {
  if (!hhmm) return '';
  const [H, M = '00'] = hhmm.split(':');
  const hour = Number(H);
  const min = Number(M);
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  const ampm = hour < 12 ? 'am' : 'pm';
  return min
    ? `${h12}:${String(min).padStart(2, '0')}${ampm}`
    : `${h12}${ampm}`;
};

const zoneAbbr = (zone?: string) => (zone ? moment.tz(zone).format('z') : '');
export const rowsToOperatingHours = (rows: HoursRow[], tz?: string) => {
  const abbr = zoneAbbr(tz);
  return rows
    .filter((r) => r.startDay && r.endDay && r.startTime && r.endTime)
    .map((r) =>
      `${r.startDay}-${r.endDay}, ${hhmmToLabel(r.startTime)}-${hhmmToLabel(
        r.endTime
      )} ${abbr}`.trim()
    )
    .join(', ');
};
